<template>
  <gas-page-shell :breadcrumb="['设备管理', '设备统计']">
    <div class="search-section">
      <div class="section-title"><span class="title-bar"></span>检索条件</div>
      <el-form :inline="true" :model="query" size="small" label-width="96px" class="search-form search-form-layout">
        <el-form-item label="站点名称">
          <el-select v-model="query.siteName" clearable filterable placeholder="请选择站点" style="width:160px">
            <el-option v-for="s in siteOptions" :key="s" :label="s" :value="s" />
          </el-select>
        </el-form-item>
        <el-form-item label="设备名称">
          <el-input v-model="query.deviceName" clearable placeholder="请输入设备名称" style="width:160px" @keyup.enter.native="search" />
        </el-form-item>
        <el-form-item label="设备编号">
          <el-input v-model="query.deviceCode" clearable placeholder="设备唯一编号" style="width:160px" @keyup.enter.native="search" />
        </el-form-item>
        <el-form-item label="设备类型">
          <el-select v-model="query.terminalType" clearable placeholder="全部类型" style="width:160px">
            <el-option v-for="t in deviceTypeOptions" :key="t" :label="t" :value="t" />
          </el-select>
        </el-form-item>
        <el-form-item label="项目分类">
          <el-select v-model="query.projectCategoryName" clearable filterable placeholder="全部分类" style="width:160px">
            <el-option
              v-for="c in projectCategoryOptions"
              :key="projectCategoryValue(c)"
              :label="projectCategoryLabel(c)"
              :value="projectCategoryValue(c)"
            />
          </el-select>
        </el-form-item>
        <template v-if="advancedVisible">
          <el-form-item label="所属机构">
            <el-select v-model="query.orgId" clearable placeholder="全部机构" style="width:160px">
              <el-option v-for="o in orgOptions" :key="o.orgId" :label="o.orgName" :value="o.orgId" />
            </el-select>
          </el-form-item>
          <el-form-item label="型号">
            <el-select v-model="query.model" clearable filterable placeholder="全部型号" style="width:140px">
              <el-option v-for="m in modelOptions" :key="m" :label="m" :value="m" />
            </el-select>
          </el-form-item>
          <el-form-item label="启用状态">
            <el-select v-model="query.enabledStatus" clearable placeholder="全部状态" style="width:120px">
              <el-option label="已启用" value="0" />
              <el-option label="已停用" value="1" />
            </el-select>
          </el-form-item>
        </template>
        <div class="search-actions">
          <el-button type="text" class="btn-advanced" @click="advancedVisible = !advancedVisible">
            {{ advancedVisible ? '收起检索' : '高级检索' }}
            <i :class="advancedVisible ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
          </el-button>
          <el-button icon="el-icon-refresh-left" @click="resetSearch">重置</el-button>
          <el-button type="primary" icon="el-icon-search" @click="search">查询</el-button>
        </div>
      </el-form>
    </div>

    <div class="stat-row">
      <div v-for="item in statCards" :key="item.label" class="stat-card">
        <div class="stat-icon" :style="{ background: item.bg, color: item.color }">
          <i :class="item.icon"></i>
        </div>
        <div>
          <div class="stat-label">{{ item.label }}</div>
          <div class="stat-value">{{ item.value }}</div>
        </div>
      </div>
    </div>

    <div v-loading="loading" class="chart-grid">
      <div v-for="section in pieSections" :key="section.key" class="chart-card">
        <div class="chart-title">
          {{ section.title }}
          <span v-if="section.sub" class="sub">{{ section.sub }}</span>
        </div>
        <div v-if="section.items.length" class="pie-wrap">
          <div class="pie" :class="{ donut: section.donut }" :style="{ background: pieGradient(section.items) }"></div>
          <ul class="legend">
            <li v-for="(it, i) in section.items" :key="section.key + '-' + (it.name || i)">
              <i :style="{ background: pieColor(i) }"></i>
              {{ it.name }} <em>{{ it.value }} · {{ it.percent }}%</em>
            </li>
          </ul>
        </div>
        <div v-else class="empty-tip">暂无数据</div>
      </div>
    </div>

    <div class="rank-grid">
      <div v-for="section in rankSections" :key="section.title" class="rank-card">
        <div class="rank-head">{{ section.title }} <span class="unit">{{ section.unit }}</span></div>
        <ul v-if="section.items.length">
          <li v-for="it in section.items" :key="it.rank || it.code">
            <span class="rank" :class="'r' + it.rank">{{ it.rank }}</span>
            <div class="info">
              <div class="name">{{ it.code }} - {{ it.name }}</div>
              <div class="loc">{{ it.location }}</div>
            </div>
            <span class="val" :class="section.valueClass">{{ it.value }}</span>
          </li>
        </ul>
        <div v-else class="empty-tip rank-empty">暂无数据</div>
      </div>
    </div>
  </gas-page-shell>
</template>

<script>
import GasPageShell from '@/components/GasPageShell.vue'
import gasCrudPage from '@/mixins/gasCrudPage'
import deviceTypeOptionsMixin from '@/mixins/deviceTypeOptions'
import siteOptionsMixin from '@/mixins/siteOptions'
import { gasApi } from '@/api/gas'

const COLORS = ['#1677ff', '#00b42a', '#722ed1', '#fadb14', '#f53f3f', '#86909c']

const emptyQuery = () => ({
  deviceName: '',
  terminalType: '',
  deviceCode: '',
  siteName: '',
  orgId: null,
  projectCategoryName: '',
  model: '',
  enabledStatus: ''
})

export default {
  components: { GasPageShell },
  mixins: [gasCrudPage, deviceTypeOptionsMixin, siteOptionsMixin],
  data() {
    return {
      autoLoad: false,
      loadOrgOptions: true,
      loading: false,
      advancedVisible: false,
      modelOptions: [],
      projectCategoryOptions: [],
      query: emptyQuery(),
      dashboard: {}
    }
  },
  computed: {
    typePie() {
      return this.safeArray(this.dashboard.typePie)
    },
    commPie() {
      return this.safeArray(this.dashboard.commPie)
    },
    regionPie() {
      return this.safeArray(this.dashboard.regionPie || this.dashboard.provincePie)
    },
    projectCategoryPie() {
      return this.safeArray(this.dashboard.projectCategoryPie || this.dashboard.sitePie || this.dashboard.cityPie)
    },
    stdTop10() {
      return this.safeArray(this.dashboard.stdTop10)
    },
    temperatureTop10() {
      return this.safeArray(this.dashboard.temperatureTop10)
    },
    pressureTop10() {
      return this.safeArray(this.dashboard.pressureTop10)
    },
    deviceTotal() {
      const total = this.numberValue(this.dashboard.deviceTotal, this.dashboard.commTotal)
      return total > 0 ? total : this.sumPie(this.typePie)
    },
    onlineDeviceTotal() {
      return this.numberValue(this.dashboard.onlineDeviceTotal, this.pieValueByKey(this.commPie, 'online'))
    },
    offlineDeviceTotal() {
      return this.numberValue(this.dashboard.offlineDeviceTotal, this.pieValueByKey(this.commPie, 'offline'))
    },
    statCards() {
      return [
        { label: '设备总数', value: this.formatNumber(this.deviceTotal), icon: 'el-icon-s-platform', color: '#1677ff', bg: '#e8f3ff' },
        { label: '在线设备', value: this.formatNumber(this.onlineDeviceTotal), icon: 'el-icon-circle-check', color: '#00b42a', bg: '#e8ffea' },
        { label: '离线设备', value: this.formatNumber(this.offlineDeviceTotal), icon: 'el-icon-circle-close', color: '#86909c', bg: '#f2f3f5' },
        { label: '设备类型', value: this.formatNumber(this.typePie.length), icon: 'el-icon-collection-tag', color: '#722ed1', bg: '#f9f0ff' }
      ]
    },
    pieSections() {
      return [
        { key: 'type', title: '设备类型统计', items: this.typePie },
        { key: 'comm', title: '设备通信状态', sub: `设备总数 ${this.formatNumber(this.deviceTotal)}`, items: this.commPie, donut: true },
        { key: 'region', title: '设备区域分布', items: this.regionPie },
        { key: 'projectCategory', title: '项目分类', items: this.projectCategoryPie }
      ]
    },
    rankSections() {
      return [
        { title: '标况累积指标 Top10', unit: 'Nm³', items: this.stdTop10, valueClass: '' },
        { title: '温度指标 Top10', unit: '°C', items: this.temperatureTop10, valueClass: 'temp' },
        { title: '压力指标 Top10', unit: 'Kpa', items: this.pressureTop10, valueClass: 'press' }
      ]
    }
  },
  created() {
    this.loadDeviceTypeOptions()
    this.loadSiteOptions()
    this.loadProjectCategoryOptions()
    this.loadModelOptions()
    this.loadDashboard()
  },
  methods: {
    async loadProjectCategoryOptions() {
      try {
        const res = await gasApi.projectCategory.options()
        this.projectCategoryOptions = res.data || []
      } catch (e) {
        this.projectCategoryOptions = []
      }
    },
    projectCategoryLabel(row) {
      return typeof row === 'string' ? row : (row && row.categoryName) || ''
    },
    projectCategoryValue(row) {
      return typeof row === 'string' ? row : (row && row.categoryName) || ''
    },
    async loadModelOptions() {
      try {
        const res = await gasApi.device.modelOptions()
        this.modelOptions = res.data || []
      } catch (e) {
        this.modelOptions = []
      }
    },
    buildQuery() {
      const p = { ...this.query }
      Object.keys(p).forEach(k => {
        if (p[k] === '' || p[k] === null) delete p[k]
      })
      return p
    },
    async loadDashboard() {
      this.loading = true
      try {
        const res = await gasApi.terminal.statsDashboard(this.buildQuery())
        this.dashboard = res.data || {}
      } finally {
        this.loading = false
      }
    },
    search() {
      this.loadDashboard()
    },
    resetSearch() {
      this.advancedVisible = false
      this.query = emptyQuery()
      this.loadDashboard()
    },
    safeArray(rows) {
      return Array.isArray(rows) ? rows : []
    },
    numberValue(...values) {
      for (const value of values) {
        const n = Number(value)
        if (Number.isFinite(n)) return n
      }
      return 0
    },
    formatNumber(value) {
      return this.numberValue(value).toLocaleString('zh-CN')
    },
    sumPie(rows) {
      return this.safeArray(rows).reduce((sum, row) => sum + this.numberValue(row.value), 0)
    },
    pieValueByKey(rows, key) {
      const hit = this.safeArray(rows).find(row => row.key === key)
      return hit ? this.numberValue(hit.value) : 0
    },
    pieColor(index) {
      return COLORS[index % COLORS.length]
    },
    pieGradient(rows) {
      const items = this.safeArray(rows)
      if (!items.length) return '#e5e6eb'
      let acc = 0
      const parts = items.map((it, i) => {
        const start = acc
        acc += this.numberValue(it.percent)
        return `${this.pieColor(i)} ${start}% ${acc}%`
      })
      return `conic-gradient(${parts.join(', ')})`
    }
  }
}
</script>

<style scoped>
.search-section {
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 4px;
  padding: 16px 16px 4px;
  margin-bottom: 16px;
}
.section-title {
  display: flex;
  align-items: center;
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
  margin-bottom: 12px;
}
.title-bar {
  width: 3px;
  height: 14px;
  background: #1677ff;
  border-radius: 2px;
  margin-right: 8px;
}
.search-form-layout {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
}
.search-form-layout >>> .el-form-item {
  margin-bottom: 12px;
  margin-right: 0;
}
.search-actions {
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  margin-bottom: 12px;
  white-space: nowrap;
}
.search-actions .el-button + .el-button {
  margin-left: 8px;
}
.btn-advanced {
  padding-left: 0;
  margin-right: 4px;
}
.stat-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}
.stat-card {
  min-height: 88px;
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  padding: 18px;
  display: flex;
  align-items: center;
  gap: 14px;
}
.stat-icon {
  width: 46px;
  height: 46px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 22px;
}
.stat-label {
  color: #86909c;
  font-size: 13px;
}
.stat-value {
  margin-top: 7px;
  font-size: 26px;
  font-weight: 600;
  line-height: 1.1;
  color: #1d2129;
}
.chart-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}
.chart-card {
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  padding: 16px;
}
.chart-title { font-weight: 600; margin-bottom: 12px; }
.chart-title .sub { font-weight: 400; color: #86909c; font-size: 13px; margin-left: 8px; }
>>> .pie-wrap { display: flex; align-items: center; gap: 24px; }
>>> .pie { width: 160px; height: 160px; border-radius: 50%; flex-shrink: 0; }
>>> .pie.donut {
  mask: radial-gradient(circle at center, transparent 45%, #000 46%);
  -webkit-mask: radial-gradient(circle at center, transparent 45%, #000 46%);
}
>>> .legend { list-style: none; padding: 0; margin: 0; font-size: 13px; color: #4e5969; line-height: 2; }
>>> .legend i { display: inline-block; width: 8px; height: 8px; border-radius: 2px; margin-right: 6px; }
>>> .legend em { font-style: normal; color: #86909c; margin-left: 4px; }
>>> .empty-tip {
  min-height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #86909c;
  font-size: 13px;
  background: #f7f8fa;
  border: 1px dashed #e5e6eb;
  border-radius: 4px;
}
.rank-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}
>>> .rank-card {
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  padding: 12px 16px;
}
>>> .rank-head { font-weight: 600; margin-bottom: 8px; }
>>> .unit { float: right; font-weight: 400; color: #86909c; font-size: 12px; }
>>> .rank-card ul { list-style: none; padding: 0; margin: 0; }
>>> .rank-card li { display: flex; align-items: center; gap: 10px; padding: 8px 0; border-bottom: 1px solid #f2f3f5; font-size: 13px; }
>>> .rank { width: 22px; height: 22px; border-radius: 50%; background: #f2f3f5; text-align: center; line-height: 22px; font-size: 12px; flex-shrink: 0; }
>>> .rank.r1 { background: #ffd666; }
>>> .rank.r2 { background: #e8e8e8; }
>>> .rank.r3 { background: #ffbb96; }
>>> .info { flex: 1; min-width: 0; }
>>> .name { font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
>>> .loc { font-size: 12px; color: #86909c; }
>>> .val { font-weight: 600; }
>>> .val.temp { color: #ff7d00; }
>>> .val.press { color: #722ed1; }
>>> .rank-empty { min-height: 300px; }
@media (max-width: 1200px) {
  .stat-row, .chart-grid, .rank-grid { grid-template-columns: 1fr; }
}
</style>
